// Week view — 7-day grid with all-day strip + hour grid.
//
// Drop zones (consumed by useEventDrag in the parent):
//   data-drop-day      — day header (all-day band shifts)
//   data-drop-hour-col — day column (timed event time changes)
//                         carries data-drop-start-hour + data-drop-row-h
//                         so the hook back-computes the dropped hour
//                         from the pointer's Y offset.

import { useEffect, useRef } from "preact/hooks";
import type { Calendar, CalendarEvent } from "../../lib/rpc";
import {
  CAL_DAYS, allDayRange, colorFor, dateKey, daysFrom, eventDayHours,
  fmtTime, layoutOverlaps, sameDay, weekStart,
} from "../../lib/calendar-utils";
import type { EventDragController } from "../../lib/use-event-drag";
import { QueuedDot } from "../../components/queued-dot";

interface WeekViewProps {
  anchor: Date;
  today: Date;
  events: CalendarEvent[];
  calendars: Calendar[];
  onSelect: (evt: CalendarEvent) => void;
  onTapEmpty: (day: Date, hour: number) => void;
  onJumpDay: (day: Date) => void;
  drag: EventDragController;
}

// Full-day window so an early-morning (or pre-dawn) event doesn't
// land at negative `top` and bleed upward past .cal-grid into the
// all-day strip. The view auto-scrolls to ~6 AM on first mount
// (see effect below) so the user still lands on the "useful" part
// of the day instead of staring at empty midnight rows.
const HOUR_START = 0;
const HOUR_END = 23;
const ROW_H = 44;
// Where to land scrollTop on first mount: ~6 AM gives a small buffer
// above 7 AM where most days actually start.
const DEFAULT_SCROLL_HOUR = 6;

export function WeekView({ anchor, today, events, calendars, onSelect, onTapEmpty, onJumpDay, drag }: WeekViewProps) {
  const wkStart = weekStart(anchor);
  const days = daysFrom(wkStart, 7);
  const hours: number[] = [];
  for (let h = HOUR_START; h <= HOUR_END; h++) hours.push(h);

  const allDay = events.filter((e) => e.allDay);
  const timed = events.filter((e) => !e.allDay);

  // Auto-scroll to the morning on first mount of this week-anchor pair
  // so the user lands at usable hours instead of midnight. The
  // scrolling element differs by viewport: on mobile .cal-hscroll
  // owns both axes; on desktop the inner .cal-grid keeps its own
  // overflow-y. Set scrollTop on whichever actually has overflow.
  const hscrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const targetTop = (DEFAULT_SCROLL_HOUR - HOUR_START) * ROW_H;
    const tryScroll = (el: HTMLDivElement | null) => {
      if (!el) return false;
      if (getComputedStyle(el).overflowY === "auto" || getComputedStyle(el).overflowY === "scroll") {
        el.scrollTop = targetTop;
        return true;
      }
      return false;
    };
    if (!tryScroll(hscrollRef.current)) tryScroll(gridRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wkStart.getTime()]);

  return (
    <div className="cal-wrap">
      {/* cal-hscroll is the shared horizontal-scroll container. On mobile
       *  the day header + all-day strip + hour grid all exceed the
       *  viewport width together — keeping them as children of one
       *  overflow-x:auto wrapper means they pan as a unit so columns
       *  stay aligned with their day labels. On desktop the wrapper is
       *  a plain flex pass-through (no horizontal overflow). */}
      <div className="cal-hscroll" ref={hscrollRef}>
      <div className="cal-days">
        <div className="cal-time-col" />
        {days.map((d, i) => (
          <div
            key={d.toISOString()}
            className="cal-day-hd"
            data-today={sameDay(d, today)}
            data-drop-day={dateKey(d)}
            onClick={() => onJumpDay(d)}
            title={`Open ${d.toDateString()}`}
          >
            <div className="cal-day-name">{CAL_DAYS[i]}</div>
            <div className="cal-day-date">{d.getDate()}</div>
          </div>
        ))}
      </div>

      {allDay.length > 0 && (
        <div className="cal-allday">
          <div className="cal-time-col">
            <div className="cal-allday-label">all-day</div>
          </div>
          <div className="cal-allday-grid">
            {allDay.map((e) => {
              const range = allDayRange(e);
              if (!range) return null;
              const startIdx = Math.max(0, Math.floor((range.start.getTime() - wkStart.getTime()) / 86_400_000));
              const endIdx = Math.min(6, Math.floor((range.end.getTime() - wkStart.getTime()) / 86_400_000));
              if (endIdx < 0 || startIdx > 6) return null;
              return (
                <div
                  key={e.id}
                  className="cal-allday-evt"
                  data-dragging={drag.draggingId === e.id}
                  onPointerDown={drag.onPointerDown(e)}
                  style={{
                    gridColumn: `${startIdx + 1} / ${endIdx + 2}`,
                    ["--evt" as any]: `var(--${colorFor(e, calendars)})`,
                  }}
                  onClick={() => onSelect(e)}
                >
                  <span className="cal-allday-dot" />
                  <span className="cal-allday-title">
                    {e.title}
                    <QueuedDot queued={e._queued} />
                  </span>
                  {e.location && <span className="cal-allday-loc">· {e.location}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="cal-grid" ref={gridRef}>
        <div className="cal-time-col">
          {hours.map((h) => (
            <div key={h} className="cal-time-row" style={{ height: ROW_H }}>
              <span>{fmtTime(h)}</span>
            </div>
          ))}
        </div>
        {days.map((d, dayIdx) => {
          const dayEvents = timed
            .map((e) => {
              const span = eventDayHours(e, d);
              return span ? { evt: e, start: span.start, end: span.end } : null;
            })
            .filter((x): x is { evt: CalendarEvent; start: number; end: number } => x !== null);
          const placed = layoutOverlaps(dayEvents);
          const isToday = sameDay(d, today);
          return (
            <div
              key={dayIdx}
              className="cal-day-col"
              data-today={isToday}
              data-drop-hour-col={dateKey(d)}
              data-drop-start-hour={HOUR_START}
              data-drop-row-h={ROW_H}
            >
              {hours.map((h) => (
                <div
                  key={h}
                  className="cal-hour-cell"
                  style={{ height: ROW_H }}
                  onClick={() => onTapEmpty(d, h)}
                />
              ))}
              {placed.map(({ evt, col, cols }) => {
                const span = { start: evt.start, end: evt.end };
                const top = (span.start - HOUR_START) * ROW_H;
                const height = (span.end - span.start) * ROW_H - 3;
                const widthPct = 100 / cols;
                return (
                  <div
                    key={evt.evt.id}
                    className="cal-evt"
                    data-narrow={cols > 1}
                    data-dragging={drag.draggingId === evt.evt.id}
                    onPointerDown={drag.onPointerDown(evt.evt)}
                    style={{
                      top, height,
                      left: `calc(${col * widthPct}% + 2px)`,
                      width: `calc(${widthPct}% - 4px)`,
                      ["--evt" as any]: `var(--${colorFor(evt.evt, calendars)})`,
                    }}
                    onClick={(e) => { e.stopPropagation(); onSelect(evt.evt); }}
                  >
                    <div className="cal-evt-title">
                      {evt.evt.title}
                      <QueuedDot queued={evt.evt._queued} />
                    </div>
                    <div className="cal-evt-time">{fmtTime(span.start)} – {fmtTime(span.end)}</div>
                    {evt.evt.location && cols === 1 && (
                      <div className="cal-evt-loc">{evt.evt.location}</div>
                    )}
                  </div>
                );
              })}
              {isToday && <NowLine startHour={HOUR_START} rowH={ROW_H} />}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}

function NowLine({ startHour, rowH }: { startHour: number; rowH: number }) {
  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;
  if (hour < startHour || hour > HOUR_END + 1) return null;
  const top = (hour - startHour) * rowH;
  return <div className="cal-now-line" style={{ top }} />;
}
